import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, RefreshCw } from 'lucide-react';
import type { AnyViewEnvelope } from '@shared/types/views';
import type { AppRecordDoneWork, ThreadTopicSummary } from '@shared/types/thread-work';
import { Button } from '@/components/ui';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { ViewRouter } from '@/components/views/ViewRouter';
import { useThreadStateRealtime } from '@/components/activity/useThreadStateRealtime';
import { AppTaskProposal } from '@/components/apps/AppTaskProposal';
import { AppToolProposalLink } from '@/components/apps/AppToolProposalLink';
import { AppRecordSetAside } from '@/components/apps/AppRecordSetAside';
import { AppRecordPassTo } from '@/components/apps/AppRecordPassTo';
import { AppRecordDoneLine, AppRecordRunning } from '@/components/apps/AppRecordDoneLine';
import { loadOpenHandoffTargets, useTeam } from '@/lib/work-handoffs';
import { type AppRecordActionsView, planAppRecord, readAppRecordActions } from '@/lib/app-record-actions';

const PRIMARY_LINK_CLASS = 'inline-flex items-center rounded-lg bg-domino-aubergine px-4 py-2 text-sm font-semibold text-white hover:bg-domino-prune';

/** What was done on the object, and which result is open in place. */
interface DoneProps {
  /** Newest first. */
  runs: AppRecordDoneWork[];
  opened: string | null;
  onToggle: (actionRunId: string) => void;
  now: Date;
  nameOf: (userId: string) => string | null;
}

const DoneLines = ({ runs, opened, onToggle, now, nameOf }: DoneProps) => <>
  {runs.map((run) => (
    <AppRecordDoneLine key={run.actionRunId} run={run} open={opened === run.actionRunId} onToggle={() => onToggle(run.actionRunId)}
      now={now} who={nameOf(run.requesterUserId)} />
  ))}
</>;

const NextAction = ({ topic, onDone, onSetAside, keys, passedTo, done, fresh }: {
  topic: ThreadTopicSummary;
  onDone: (result: AnyViewEnvelope | null) => void;
  /** Set aside or passed: the object may have nothing left to handle. */
  onSetAside: () => void;
  /** In a séance, the first action answers to L (launch), E (set aside) and R (pass). */
  keys: boolean;
  /** Relais: the colleague it was passed to; it waits with them. */
  passedTo: string | null;
  done: Omit<DoneProps, 'runs'> & { runs: AppRecordDoneWork[] };
  /** The result just produced here, until its run is read back. */
  fresh: AnyViewEnvelope | null;
}) => {
  const reason = topic.proposal?.rationale ?? topic.decision?.reason ?? null;
  const short = topic.proposal?.whyNow ?? null;
  const proposal = topic.visibleState === 'actionable' && topic.proposal?.status === 'active' ? topic.proposal : null;
  // A task that ran an action goes on with the step it now proposes.
  const last = done.runs[0];
  const title = last && topic.proposal && topic.proposal.id !== last.proposalId ? topic.proposal.ctaLabel : topic.title;
  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <p className="text-sm font-semibold text-domino-ink">{title}</p>
      {short ? (
        <>
          <p className="mt-0.5 text-sm leading-6 text-domino-inkSoft">{short}</p>
          {reason && (
            <details className="group mt-1">
              <summary className="flex w-fit cursor-pointer list-none items-center gap-1 text-xs text-domino-inkSoft hover:text-domino-ink">
                <ChevronRight className="h-3.5 w-3.5 transition group-open:rotate-90" aria-hidden="true" />Pourquoi ?
              </summary>
              <p className="mt-1 text-sm leading-6 text-domino-inkSoft">{reason}</p>
            </details>
          )}
        </>
      ) : reason && <p className="mt-0.5 line-clamp-2 text-sm leading-6 text-domino-inkSoft" title={reason}>{reason}</p>}
      {passedTo && <p className="mt-2 text-xs text-domino-aubergine">Passée à {passedTo}</p>}
      {topic.visibleState === 'running' && <AppRecordRunning />}
      {topic.visibleState !== 'running' && !passedTo && (
        <div className="mt-3 flex flex-wrap items-start gap-2">
          {proposal?.executorKind === 'tool' && <AppToolProposalLink topic={topic} proposal={proposal} className={PRIMARY_LINK_CLASS} hotkey={keys ? 'l' : undefined} />}
          {(proposal?.executorKind === 'recipe' || proposal?.executorKind === 'capability') && (
            <AppTaskProposal topic={topic} proposal={proposal} className="space-y-2" hotkey={keys ? 'l' : undefined}
              onDone={(response) => onDone(response.status === 'completed' ? response.viewEnvelope : null)} />
          )}
          <AppRecordSetAside topic={topic} onDone={onSetAside} hotkey={keys ? 'e' : undefined} />
          <AppRecordPassTo topic={topic} onDone={onSetAside} hotkey={keys ? 'r' : undefined} />
        </div>
      )}
      {fresh && <div className="mt-3"><ViewRouter payload={fresh} /></div>}
      <DoneLines {...done} />
    </li>
  );
};

const ActionsSkeleton = () => (
  <SkeletonGroup label="Prochaines actions en préparation" className="space-y-3 rounded-xl border border-domino-cardBorder bg-domino-white p-4">
    {[0, 1].map((index) => (
      <div key={index} className="space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-8 w-44" />
      </div>
    ))}
  </SkeletonGroup>
);

/**
 * N1/N2: what to do next with this object, planned by Domino's next-step
 * planner at the end of the launch that created or changed it, or when the
 * person asks. Opening the object never plans. An action that read an older
 * state of the object stays shown, with « Actualiser ». Each one is launched
 * or set aside; in a séance, the object handled leads to the next one.
 */
export const AppRecordNextActions = ({ recordId, session }: {
  recordId: string;
  /** In a séance: called once the last open action of the object was set aside. */
  session?: { onEmptied: () => void };
}) => {
  const [view, setView] = useState<AppRecordActionsView | null>(null);
  const [passed, setPassed] = useState<Record<string, string>>({});
  const team = useTeam();
  const setAsideRef = useRef(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  /** A result just produced, shown until its run is read back (then opened in place). */
  const [fresh, setFresh] = useState<{ topicId: string; envelope: AnyViewEnvelope; known: Set<string> } | null>(null);

  const reload = useCallback(() => {
    void readAppRecordActions(recordId)
      .then(async (next) => {
        setPassed(await loadOpenHandoffTargets(next?.actions.map((topic) => topic.id) ?? []));
        setView(next);
        setError(null);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : 'Les prochaines actions n’ont pas pu être chargées.'));
  }, [recordId]);
  const plan = useCallback(() => {
    setError(null);
    setAsking(true);
    void planAppRecord(recordId).then(reload)
      .catch((cause) => setError(cause instanceof Error ? cause.message : 'Les prochaines actions n’ont pas pu être demandées.'))
      .finally(() => setAsking(false));
  }, [recordId, reload]);

  useEffect(() => { setView(null); reload(); }, [reload]);
  useEffect(() => {
    if (!view || !setAsideRef.current) return;
    setAsideRef.current = false;
    if (view.actions.every((topic) => topic.id in passed)) session?.onEmptied();
  }, [view, passed, session]);
  const setAside = useCallback(() => { setAsideRef.current = true; reload(); }, [reload]);
  useEffect(() => {
    if (!fresh || !view) return;
    const run = view.done.find((item) => item.topicId === fresh.topicId && !fresh.known.has(item.actionRunId));
    if (!run) return;
    setOpened(run.actionRunId);
    setFresh(null);
  }, [view, fresh]);
  const toggle = useCallback((actionRunId: string) => setOpened((current) => (current === actionRunId ? null : actionRunId)), []);
  const nameOf = useCallback((userId: string) => (userId === team?.me ? null : team?.members.find((member) => member.userId === userId)?.name ?? null), [team]);
  useThreadStateRealtime({ enabled: Boolean(view?.threadId), threadId: view?.threadId ?? null, onRefresh: reload });

  if (error) {
    return <p role="alert" className="text-sm text-[var(--domino-critical)]">{error} <button type="button" className="underline" onClick={reload}>Réessayer</button></p>;
  }
  if (!view) return <ActionsSkeleton />;
  const preparing = view.preparing || asking;
  const now = new Date();
  const actionIds = new Set(view.actions.map((topic) => topic.id));
  // Tasks closed after their action: what they did stays here.
  const closed = view.done.filter((run) => !actionIds.has(run.topicId));
  const doneOf = (topicId: string) => ({ runs: view.done.filter((run) => run.topicId === topicId), opened, onToggle: toggle, now, nameOf });
  const closedLines = closed.length > 0 && (
    <section aria-label="Fait sur cette fiche" className="rounded-xl border border-domino-cardBorder bg-domino-white px-4 pb-3 pt-1">
      <DoneLines runs={closed} opened={opened} onToggle={toggle} now={now} nameOf={nameOf} />
    </section>
  );
  if (view.actions.length === 0) {
    const head = preparing ? <ActionsSkeleton />
      : view.failed ? (
        <p role="alert" className="text-sm text-[var(--domino-critical)]">
          Les prochaines actions n’ont pas pu être préparées. <Button variant="secondary" size="sm" onClick={plan}>Réessayer</Button>
        </p>
      )
      // A current plan with no action is the planner's answer: nothing to show.
      : view.current ? null : <Button variant="secondary" size="sm" onClick={plan}>Proposer la suite</Button>;
    if (!closedLines) return head;
    return <div className="space-y-3">{head}{closedLines}</div>;
  }
  return (<div className="space-y-3">
    <section aria-label="Prochaines actions" className="relative rounded-xl border border-domino-cardBorder bg-domino-white p-4">
      {(!view.current || preparing) && (
        <button type="button" onClick={plan} disabled={preparing} aria-label="Actualiser" title="Actualiser"
          className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-ink disabled:cursor-default">
          <RefreshCw className={preparing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
        </button>
      )}
      <ul className="divide-y divide-domino-cardBorder pr-8">
        {view.actions.map((topic) => (
          <NextAction key={topic.id} topic={topic} onSetAside={setAside} done={doneOf(topic.id)}
            fresh={fresh?.topicId === topic.id ? fresh.envelope : null}
            onDone={(envelope) => {
              if (envelope) {
                setOpened(null);
                setFresh({ topicId: topic.id, envelope, known: new Set(view.done.filter((run) => run.topicId === topic.id).map((run) => run.actionRunId)) });
              }
              reload();
            }}
            passedTo={passed[topic.id] ? team?.members.find((member) => member.userId === passed[topic.id])?.name ?? 'un collègue' : null}
            keys={Boolean(session) && topic.id === view.actions.find((action) => action.visibleState !== 'running' && !(action.id in passed))?.id} />
        ))}
      </ul>
    </section>
    {closedLines}
  </div>);
};
